import React, { useState, useEffect, useCallback } from 'react'
import { Card, Error, Select, Skeleton, Form } from 'components/common'
import { useAllCorporateCustomersQuery } from '../../../lib/graphql/storeadmin/types-and-hooks'

type SelectCustomer = {
  value?: string
  label?: string
}

type Props = {
  customerSelection: string[]
  canOverlap: boolean
  form: any
  isEntire: boolean
}

export const CustomerQualificationCard: React.FC<Props> = (props) => {
  const { customerSelection, canOverlap, form, isEntire } = props
  const [options, setOptions] = useState([])
  const { data, loading, error } = useAllCorporateCustomersQuery({
    fetchPolicy: 'network-only',
  })

  /**
   * ディスカウントが適用されているかチェックする
   */
  const isAppliedDiscount = (customer): boolean => {
    return customer.appliedDiscount !== null
  }

  /**
   * 全体に対するディスカウントが適用されているかチェックする
   */
  const hasStoreDiscount = (customer): boolean => {
    return customer.appliedDiscount.discounts.edges.some(
      ({ node }) => node.product === null && node.canOverlap === false
    )
  }

  /**
   * 適用されているディスカウントがすべて重複可能なものかチェックする
   */
  const areAllDiscountsOverlapable = (customer): boolean => {
    return customer.appliedDiscount.discounts.edges.every(
      ({ node }) => node.canOverlap === true
    )
  }

  /**
   * ステータスがアーカイブかチェックする
   */
  const isArchived = (customer, productId: string): boolean => {
    const result = customer.appliedDiscount.discounts.edges.find(
      ({ node }) => node.product?.id === productId && node.status === 'ARCHIVED'
    )
    return !!result
  }

  /**
   * 適用されているディスカウントのステータスが全てアーカイブであるかチェックする
   */
  const areAllDiscountsArchived = (customer): boolean => {
    const result = customer.appliedDiscount.discounts.edges.every(
      ({ node }) => node.status === 'ARCHIVED'
    )
    return !!result
  }

  /**
   * 同じ施設のディスカウントが適用されているかチェックする
   */
  const isSameProductDiscountApplied = (
    customer,
    productId: string
  ): boolean => {
    return customer.appliedDiscount.discounts.edges.some(
      ({ node }) => node.product?.id === productId
    )
  }

  /**
   * 全体を選択した場合に表示するユーザーの配列を生成する
   */
  const getNonDiscountedOrOverlapableCustomers = useCallback((customers) => {
    const result = customers.filter((customer) => {
      return (
        !isAppliedDiscount(customer) ||
        areAllDiscountsArchived(customer) ||
        areAllDiscountsOverlapable(customer)
      )
    })
    return result
  }, [])

  /**
   * 施設を選択した場合に表示するユーザーの配列を生成する
   */
  const getNonSameProductDiscountCustomers = useCallback(
    (customers, productId: string, canOverlap: boolean) => {
      if (!productId) return customers
      const hasDiscount = (customer, product: string): boolean => {
        return !isAppliedDiscount(customer) || isArchived(customer, product)
      }
      const result = customers.filter((customer) => {
        // 重複可否の値によって表示するユーザーをフィルタリング
        if (canOverlap) {
          return (
            hasDiscount(customer, productId) ||
            !isSameProductDiscountApplied(customer, productId)
          )
        } else {
          return (
            hasDiscount(customer, productId) ||
            (!hasStoreDiscount(customer) &&
              !isSameProductDiscountApplied(customer, productId))
          )
        }
      })
      return result
    },
    []
  )

  // 選択されたディスカウントのカテゴリをもとにディスカウントを適用するユーザーをフィルタリングする
  useEffect(() => {
    if (!data) return
    if (isEntire) {
      const filteredCustomers = getNonDiscountedOrOverlapableCustomers(
        data.allCorporateCustomers
      )
      const newOptions = filteredCustomers.map((customer) => ({
        label: `${customer.corporationName} (${customer.lastName}${customer.firstName})`,
        value: customer.id,
      }))
      setOptions(newOptions)
    } else {
      const productId = form.getFieldValue('product')
      const filteredCustomers = getNonSameProductDiscountCustomers(
        data.allCorporateCustomers,
        productId,
        canOverlap
      )
      const newOptions = filteredCustomers.map((customer) => ({
        label: `${customer.corporationName} (${customer.lastName}${customer.firstName})`,
        value: customer.id,
      }))
      setOptions(newOptions)
    }
  }, [
    data,
    canOverlap,
    form,
    getNonSameProductDiscountCustomers,
    getNonDiscountedOrOverlapableCustomers,
    setOptions,
    isEntire,
  ])

  if (loading) return <Skeleton active />
  if (error) return Error.render(error)

  // 初期表示用のユーザーリストを生成する
  const initialCustomers: SelectCustomer[] = []
  data?.allCorporateCustomers.forEach((customer) => {
    if (!customer) return
    customerSelection?.forEach((item: string) => {
      if (customer.id === item)
        initialCustomers.push({
          label: `${customer.corporationName} (${customer.lastName}${customer.firstName})`,
          value: customer.id,
        })
    })
  })

  /**
   * ユーザーをlabelの値で検索できるようにするための関数
   */
  const handleSearch = (input, option) => {
    if (!option) return
    return option.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  return (
    <>
      <Card>
        <Form.Item
          name={'customerSelection'}
          label="ディスカウントを適用するユーザー"
          initialValue={initialCustomers}
        >
          <Select
            mode="multiple"
            placeholder="ユーザーを選択する"
            style={{ width: '100%' }}
            options={options}
            filterOption={handleSearch}
          />
        </Form.Item>
      </Card>
    </>
  )
}
